import { createContext, useContext, type ReactNode } from 'react';
import type {
  BlockLayout,
  InlineContent,
  SectionContentItem,
  SectionLayout,
  SegmentationDashboardWidget,
  WidgetGridLayout,
  WidgetInstance,
  WidgetPropsByType,
} from '@trinity/chat';

export type WidgetGridContextValue = {
  instance: WidgetGridLayout;
  setInstance: (instance: WidgetGridLayout) => void;
};

export type WidgetGridWidgetsContextValue = {
  widgetsMap: Map<number, WidgetInstance>;
  widgetsLoading: boolean;
  widgets: WidgetInstance[];
};

export type NewWidgetGridBlock = Omit<BlockLayout, 'id' | 'sections'> & {
  sections?: NewWidgetGridSection[];
};

export type NewWidgetGridSection = Omit<SectionLayout, 'id' | 'contentItems'>;

export type NewWidgetGridWidget = WidgetInstance;

export type WidgetGridEditingContextValue = {
  editing: boolean;
  setEditing: (editing: boolean) => void;

  addBlockToGrid: (newBlock: NewWidgetGridBlock, index?: number) => void;
  addWidgetAsBlock: (blockInsertIndex: number, newWidget: NewWidgetGridWidget) => void;
  addInlineContentAsBlock: (blockInsertIndex: number, content: InlineContent) => void;
  removeBlockFromGrid: (blockId: string) => void;

  addSectionToBlock: (blockId: string, newSection: NewWidgetGridSection, index?: number) => void;
  removeSectionFromBlock: (blockId: string, sectionId: string) => void;
  toggleWidgetWidth: (sectionId: string, widgetIndex: number) => void;

  addContentItemToSection: (sectionId: string, item: SectionContentItem, index?: number) => void;
  removeContentItemFromSection: (sectionId: string, index: number) => void;

  openWidgetPicker: (blockId: string, sectionId: string) => void;
  openWidgetPickerBlock: (blockInsertIndex: number) => void;
  closeWidgetPicker: () => void;
  widgetPickerOpen: boolean;
  widgetPickerTarget: { blockIndex?: number; blockId?: string; sectionId?: string } | null;
};

export type WidgetGridMutationContextValue = {
  onSave: (instance: WidgetGridLayout) => Promise<void>;
  onCancel: () => Promise<void>;
};

export type WidgetMapContextValue = {
  [K in SegmentationDashboardWidget]: React.ComponentType<WidgetPropsByType[K]>;
};

export const WidgetGridContext = createContext<WidgetGridContextValue>({} as WidgetGridContextValue);
export const WidgetGridWidgetsContext = createContext<WidgetGridWidgetsContextValue>(
  {} as WidgetGridWidgetsContextValue
);
export const WidgetGridLoadingContext = createContext<boolean>(true);
export const WidgetGridEditingContext = createContext<WidgetGridEditingContextValue>(
  {} as WidgetGridEditingContextValue
);
export const WidgetGridMutationContext = createContext<WidgetGridMutationContextValue>(
  {} as WidgetGridMutationContextValue
);

export const WidgetGridBlockContext = createContext<BlockLayout>({} as BlockLayout);
export const WidgetGridSectionContext = createContext<SectionLayout>({} as SectionLayout);

export const WidgetMapContext = createContext<WidgetMapContextValue>({} as WidgetMapContextValue);

export type WidgetGridCategory = 'chart' | 'kpi' | 'inline-text' | 'quote' | 'media';

const DEFAULT_WIDGET_GRID_CATEGORIES: WidgetGridCategory[] = ['inline-text', 'chart', 'kpi', 'quote'];

export const WidgetGridCategoriesContext = createContext<WidgetGridCategory[]>(
  DEFAULT_WIDGET_GRID_CATEGORIES
);

export function useWidgetGridCategoriesContext(): WidgetGridCategory[] {
  return useContext(WidgetGridCategoriesContext);
}

export function useWidgetGridContext() {
  const context = useContext(WidgetGridContext);
  if (!context) {
    throw new Error('useWidgetGridContext must be used within a WidgetGridProvider');
  }
  return context;
}

export function useWidgetGridWidgetsContext() {
  const context = useContext(WidgetGridWidgetsContext);
  if (!context) {
    throw new Error('useWidgetGridWidgetsContext must be used within a WidgetGridProvider');
  }
  return context;
}

export function useWidgetGridLoadingContext() {
  const context = useContext(WidgetGridLoadingContext);
  if (context === undefined) {
    throw new Error('useWidgetGridLoadingContext must be used within a WidgetGridProvider');
  }
  return context;
}

export function useWidgetGridEditingContext() {
  const context = useContext(WidgetGridEditingContext);
  if (!context) {
    throw new Error('useWidgetGridEditingContext must be used within a WidgetGridProvider');
  }
  return context;
}

export function useWidgetGridMutationContext() {
  const context = useContext(WidgetGridMutationContext);
  if (!context) {
    throw new Error('useWidgetGridMutationContext must be used within a WidgetGridProvider');
  }
  return context;
}

export function useWidgetMapContext() {
  const context = useContext(WidgetMapContext);
  if (!context) {
    throw new Error('useWidgetMapContext must be used within a WidgetMapContextProvider');
  }
  return context;
}

export type WidgetGridItemFooterRenderer = (widget: WidgetInstance) => ReactNode;

export const WidgetGridItemFooterContext = createContext<WidgetGridItemFooterRenderer>(() => null);

export function useWidgetGridItemFooterContext(): WidgetGridItemFooterRenderer {
  return useContext(WidgetGridItemFooterContext);
}
